import React from 'react';
import {
	Button,
	Col,
	Input,
	Row,
	Label,
	ModalBody,
	Form,
	FormFeedback,
	ModalFooter,
	Spinner,
} from 'reactstrap';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import { MultiSelect, Select } from '@mantine/core';
import { sanitizeStrArr } from '../utils';
import useFiscalitationForm from './useFiscalitationForm';
import ResponsableCargoFields from './ResponsableCargoFields';
import FiscalizadoresField from './FiscalizadoresField';

const FiscalitationForm = ({
	dataFiscalizacion,
	isOpen,
	isEdit,
	handleToggleModal,
	handleSave,
	establishmentOptions,
	inspectionTypeOptions,
	inspectionAuthorityOptions,
	personOptions,
	managementPositionOptions,
	fiscalizerOptions,
}) => {
	const {
		isAdding,
		isUpdating,
		canCerrar,
		validation,
		selectedFile,
		setSelectedFile,
		selectedClosureFile,
		setSelectedClosureFile,
		enEspera,
		setEnEspera,
		submitModeRef,
		submittingRef,
		estSelectData,
		typeSelectData,
		authoritySelectData,
		personSelectData,
		positionSelectData,
		fiscalizadorSelectData,
		isNewPerson,
		cargoDisplayName,
		setCreatedPersons,
		setCreatedPositions,
		setCreatedFiscalizadores,
	} = useFiscalitationForm({
		dataFiscalizacion,
		isOpen,
		handleSave,
		establishmentOptions,
		inspectionTypeOptions,
		inspectionAuthorityOptions,
		personOptions,
		managementPositionOptions,
		fiscalizerOptions,
	});

	const { values, errors, submitCount, setFieldValue, handleChange, handleBlur } = validation;

	return (
		<Form className='tablelist-form' onSubmit={e => e.preventDefault()}>
			<ModalBody className='modal-body'>
				<Row className='g-3'>
					<Col lg={4} className='mb-3'>
						<Label className='form-label'>
							Fecha de Fiscalización <span className='text-danger'>*</span>
						</Label>
						<DatesProvider settings={{ locale: 'es-mx', firstDayOfWeek: 1, weekendDays: [0] }}>
							<DatePickerInput
								placeholder='Seleccione fecha'
								value={values.fecha}
								onChange={value => setFieldValue('fecha', value)}
								valueFormat='DD-MM-YYYY'
								maxDate={new Date()}
								styles={{ input: { fontSize: '14px' } }}
								clearable
								error={submitCount > 0 && errors.fecha ? errors.fecha : null}
							/>
						</DatesProvider>
					</Col>

					<Col lg={4} className='mb-3'>
						<Label className='form-label'>
							Establecimiento <span className='text-danger'>*</span>
						</Label>
						<Select
							data={estSelectData}
							value={values.establecimiento_id}
							onChange={value => setFieldValue('establecimiento_id', value || '')}
							placeholder='Seleccione establecimiento'
							searchable
							clearable
							error={submitCount > 0 && errors.establecimiento_id ? errors.establecimiento_id : null}
						/>
					</Col>

					<Col lg={4} className='mb-3'>
						<Label className='form-label'>
							Tipo de Fiscalización <span className='text-danger'>*</span>
						</Label>
						<Select
							data={typeSelectData}
							value={values.tipo}
							onChange={value => setFieldValue('tipo', value || '')}
							placeholder='Seleccione tipo de fiscalización'
							searchable
							clearable
							error={submitCount > 0 && errors.tipo ? errors.tipo : null}
						/>
					</Col>

					<Col lg={4} className='mb-3'>
						<Label className='form-label'>
							Entidad Fiscalizadora <span className='text-danger'>*</span>
						</Label>
						<MultiSelect
							data={authoritySelectData}
							value={sanitizeStrArr(values.entidades)}
							onChange={value => setFieldValue('entidades', sanitizeStrArr(value))}
							placeholder='Seleccione entidades'
							searchable
							clearable
							error={submitCount > 0 && errors.entidades ? errors.entidades : null}
						/>
					</Col>

					<ResponsableCargoFields
						validation={validation}
						personSelectData={personSelectData}
						positionSelectData={positionSelectData}
						isNewPerson={isNewPerson}
						cargoDisplayName={cargoDisplayName}
						setCreatedPersons={setCreatedPersons}
						setCreatedPositions={setCreatedPositions}
					/>

					<FiscalizadoresField
						validation={validation}
						fiscalizadorSelectData={fiscalizadorSelectData}
						setCreatedFiscalizadores={setCreatedFiscalizadores}
					/>

					<Col lg={12} className='mb-3'>
						<Label className='form-label'>Documento de Acta de Obervaciones</Label>
						<Input
							id='documento'
							className='form-control'
							type='file'
							onChange={e => setSelectedFile(e.target.files[0] || null)}
						/>
						{selectedFile && (
							<small className='text-muted'>Archivo seleccionado: {selectedFile.name}</small>
						)}
					</Col>

					<Col lg={12} className='mb-2'>
						<div className='form-check'>
							<input
								className='form-check-input'
								type='checkbox'
								id='enEspera'
								checked={enEspera}
								onChange={e => {
									setEnEspera(e.target.checked);
									if (!e.target.checked) setFieldValue('review_notes', '');
								}}
							/>
							<label className='form-check-label' htmlFor='enEspera'>
								¿En espera de Observaciones?
							</label>
						</div>
					</Col>

					{enEspera && (
						<Col lg={12} className='mb-3'>
							<Label for='review_notes' className='form-label'>Observaciones</Label>
							<Input
								name='review_notes'
								id='review_notes'
								className='form-control'
								type='textarea'
								rows='4'
								placeholder='Ingrese observaciones...'
								value={values.review_notes}
								onChange={handleChange}
								onBlur={handleBlur}
							/>
						</Col>
					)}

					<Col lg={12} className='mb-3'>
						<Label for='observaciones' className='form-label'>Comentarios Adicionales</Label>
						<Input
							name='observaciones'
							id='observaciones'
							className='form-control'
							type='textarea'
							rows='4'
							placeholder='Ingrese comentarios adicionales...'
							value={values.observaciones}
							onChange={handleChange}
							onBlur={handleBlur}
							invalid={submitCount > 0 && !!errors.observaciones}
						/>
						{submitCount > 0 && errors.observaciones && (
							<FormFeedback>{errors.observaciones}</FormFeedback>
						)}
					</Col>

					<Col lg={12} className='mb-3'>
						<Label className='form-label'>Respaldo de Cierre Sin Hallazgos</Label>
						<Input
							id='finding_closure_document'
							className='form-control'
							type='file'
							onChange={e => setSelectedClosureFile(e.target.files[0] || null)}
						/>
						{selectedClosureFile && (
							<small className='text-muted'>Archivo seleccionado: {selectedClosureFile.name}</small>
						)}
					</Col>
				</Row>
			</ModalBody>

			<ModalFooter>
				<div className='d-flex justify-content-between w-100 align-items-center'>
					{canCerrar && (
						<button
							type='button'
							className='btn btn-label btn-success'
							disabled={isAdding || isUpdating}
							onClick={() => {
								if (submittingRef.current || isAdding || isUpdating) return;
								submittingRef.current = true;
								submitModeRef.current = 'close';
								validation.handleSubmit();
							}}
						>
							{isAdding || isUpdating ? (
								<i className='label-icon'><Spinner size='sm' /></i>
							) : (
								<i className='ri-checkbox-circle-line label-icon'></i>
							)}
							Cerrar Sin Hallazgos
						</button>
					)}
					<div className='hstack gap-2'>
						<Button
							type='button'
							onClick={() => {
								validation.resetForm();
								setSelectedFile(null);
								handleToggleModal();
							}}
							className='btn-danger btn-label'
						>
							<i className='ri-close-line label-icon'></i>
							Cancelar
						</Button>
						<button
							type='button'
							className='btn btn-label btn-primary'
							disabled={isAdding || isUpdating}
							onClick={() => {
								if (submittingRef.current || isAdding || isUpdating) return;
								submittingRef.current = true;
								validation.handleSubmit();
							}}
						>
							{isAdding || isUpdating ? (
								<i className='label-icon'><Spinner size='sm' /></i>
							) : (
								<i className='ri-save-line label-icon'></i>
							)}
							Guardar Fiscalización
						</button>
					</div>
				</div>
			</ModalFooter>
		</Form>
	);
};

export default FiscalitationForm;
